/**
 * Bio Gallery - Image gallery functionality for the Bio panel
 */

class BioGallery {
    constructor() {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }
    
    /**
     * Setup the gallery when DOM is ready
     */
    setup() {
        // Initialize gallery elements
        this.galleryImages = document.getElementById('bio-gallery-images');
        this.prevButton = document.getElementById('gallery-prev');
        this.nextButton = document.getElementById('gallery-next');
        this.indicators = document.getElementById('gallery-indicators');
        
        // Gallery state
        this.currentIndex = 0;
        
        // Check if we have gallery images
        if (this.galleryImages) {
            const imageWrappers = this.galleryImages.querySelectorAll('.bio-gallery-image-wrapper');
            this.totalImages = imageWrappers.length || 1;
            console.log(`Bio Gallery found ${this.totalImages} images`);
        } else {
            this.totalImages = 1; // Default if no images found
            console.warn('Bio Gallery container not found');
        }
        
        // Bind event handlers
        this.init();
        
        // Set initial state
        this.updateGalleryIndicators();
    }
    
    /**
     * Initialize gallery functionality
     */
    init() {
        // Add event listeners
        if (this.prevButton) {
            this.prevButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigate(-1);
                console.log('Previous button clicked, navigating to index:', this.currentIndex);
            });
        }
        
        if (this.nextButton) {
            this.nextButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigate(1);
                console.log('Next button clicked, navigating to index:', this.currentIndex);
            });
        }
        
        // Add indicators click events
        if (this.indicators) {
            const indicatorElements = this.indicators.querySelectorAll('.bio-gallery-indicator');
            indicatorElements.forEach((indicator, index) => {
                indicator.addEventListener('click', () => {
                    this.goToSlide(index);
                    console.log('Indicator clicked, navigating to index:', index);
                });
            });
        }
        
        // Enable keyboard navigation
        document.addEventListener('keydown', (e) => {
            // Only process keyboard events when the bio panel is active
            const bioPanel = document.querySelector('.bio-panel.active');
            if (!bioPanel) return;
            
            if (e.key === 'ArrowLeft') {
                this.navigate(-1);
            } else if (e.key === 'ArrowRight') {
                this.navigate(1);
            }
        });

        console.log('Bio Gallery initialized');
    }
    
    /**
     * Navigate to the previous or next image
     * @param {number} direction - Direction to navigate (-1 for prev, 1 for next)
     */
    navigate(direction) {
        if (this.totalImages <= 1) return; // No navigation needed if we have only 1 image
        
        // Calculate the new index based on current position and direction
        // Move 1 image at a time for more granular control
        let newIndex = this.currentIndex + direction;
        
        // Handle bounds with wrapping
        if (newIndex < 0) {
            // When going left from the beginning, go to the last image
            newIndex = this.totalImages - 1;
        } else if (newIndex >= this.totalImages) {
            newIndex = 0; // Go to first image when passing the end
        }
        
        console.log(`Navigating from ${this.currentIndex} to ${newIndex} (direction: ${direction})`);
        this.goToSlide(newIndex);
    }
    
    /**
     * Go to a specific slide by index
     * @param {number} index - The index to navigate to
     */
    goToSlide(index) {
        if (index < 0 || index >= this.totalImages) return;
        
        // Update current index
        this.currentIndex = index;
        
        // Get all image wrappers
        const imageWrappers = this.galleryImages.querySelectorAll('.bio-gallery-image-wrapper');
        if (imageWrappers.length === 0) return;
        
        // Find the target image and scroll to it
        if (imageWrappers[index]) {
            // Get the target element to scroll to
            const targetElement = imageWrappers[index];
            
            // Calculate the scroll position (offsetLeft gives position relative to parent)
            const scrollPosition = targetElement.offsetLeft;
            
            // Use scrollTo for smooth scrolling to the image
            this.galleryImages.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
            
            console.log(`Navigating gallery to index ${index}`);
            console.log(`Found ${imageWrappers.length} image wrappers`);
            console.log(`Scrolling to element at index ${index}`);
        }
        
        // Update indicators - highlight the indicator for the current slide
        this.updateIndicators();
    }
    
    /**
     * Update the active indicator
     */
    updateIndicators() {
        if (!this.indicators) return;
        
        const indicators = this.indicators.querySelectorAll('.bio-gallery-indicator');
        indicators.forEach((indicator, index) => {
            if (index === this.currentIndex) {
                indicator.classList.add('active');
            } else {
                indicator.classList.remove('active');
            }
        });
    }
    
    /**
     * Add images to the gallery
     * @param {Array} images - Array of image objects with src and alt properties
     */
    setImages(images) {
        if (!this.galleryImages || !Array.isArray(images) || images.length === 0) return;
        
        // Clear existing images
        this.galleryImages.innerHTML = '';
        
        // Add new images
        images.forEach(image => {
            const wrapper = document.createElement('div');
            wrapper.className = 'bio-gallery-image-wrapper';
            
            const img = document.createElement('img');
            img.src = image.src;
            img.alt = image.alt || 'Gallery Image';
            img.className = 'bio-gallery-image';
            
            wrapper.appendChild(img);
            this.galleryImages.appendChild(wrapper);
        });
        
        // Update gallery state
        this.totalImages = images.length;
        this.currentIndex = 0;
        
        // Reset transform
        this.galleryImages.style.transform = 'translateX(0)';
        
        // Update indicators
        this.updateGalleryIndicators();
    }
    
    /**
     * Update gallery indicators based on the number of images
     */
    updateGalleryIndicators() {
        if (!this.indicators) return;
        
        // Clear existing indicators
        this.indicators.innerHTML = '';
        
        // Add new indicators
        for (let i = 0; i < this.totalImages; i++) {
            const indicator = document.createElement('div');
            indicator.className = 'bio-gallery-indicator';
            indicator.setAttribute('data-index', i);
            
            if (i === 0) {
                indicator.classList.add('active');
            }
            
            indicator.addEventListener('click', () => this.goToSlide(i));
            this.indicators.appendChild(indicator);
        }
    }
}

// Create and export a singleton instance
const bioGallery = new BioGallery();
export default bioGallery;